import type { CountryId, CountryState, ResourceBundle, WorldState } from '../shared/types.ts';

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function sum(values: readonly number[]): number {
  let s = 0;
  for (const v of values) s += v;
  return s;
}

export function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const s = values.slice().sort((a, b) => a - b);
  const mid = s.length >> 1;
  return s.length % 2 ? (s[mid] as number) : ((s[mid - 1] as number) + (s[mid] as number)) / 2;
}

export function zeroBundle(): ResourceBundle {
  return { treasury: 0, energy: 0, food: 0 };
}

export function bundleTotal(b: ResourceBundle): number {
  return b.treasury + b.energy + b.food;
}

export function isValidBundle(b: unknown): b is ResourceBundle {
  if (typeof b !== 'object' || b === null) return false;
  const r = b as Record<string, unknown>;
  return (['treasury', 'energy', 'food'] as const).every(
    (k) => typeof r[k] === 'number' && Number.isInteger(r[k]) && (r[k] as number) >= 0 && (r[k] as number) <= 99,
  );
}

/** Deep copy of a JSON-compatible value. The engine never mutates its input state. */
export function clone<T>(value: T): T {
  return structuredClone(value);
}

export function getCountry(state: WorldState, id: CountryId): CountryState {
  const c = state.countries[id];
  if (!c) throw new Error(`Unknown country ${id}`);
  return c;
}

export function tryCountry(state: WorldState, id: string | null | undefined): CountryState | undefined {
  if (!id) return undefined;
  return state.countries[id as CountryId];
}

export function nationalIndex(c: Pick<CountryState, 'prosperity' | 'security' | 'wellbeing' | 'environment'>): number {
  return Math.round((c.prosperity + c.security + c.wellbeing + c.environment) / 4);
}

/** Exact (unrounded) National Index, for balance statistics. */
export function nationalIndexExact(c: Pick<CountryState, 'prosperity' | 'security' | 'wellbeing' | 'environment'>): number {
  return (c.prosperity + c.security + c.wellbeing + c.environment) / 4;
}

export function available(c: CountryState): ResourceBundle {
  return {
    treasury: c.treasury - c.escrow.treasury,
    energy: c.energy - c.escrow.energy,
    food: c.food - c.escrow.food,
  };
}

export function forcesTotal(f: CountryState['forces']): number {
  return f.army + f.navy + f.air + f.defenses;
}

export function isInt(v: unknown, lo = -9999, hi = 9999): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi;
}

/** Plain signed number for previews: "+3", "-2". */
export function signed(n: number): string {
  return n > 0 ? `+${n}` : `${n}`;
}
